import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import ScrollPositionedHeader, {
  AppearOnScrollPosition,
} from "../../../components/ScrollPositionedHeader";
import { InpageNavLink } from "../../../components/InPageNavMenu";
import { PermissionResource, Resources } from "../../Permissions";

export default function ITConnectionHeader({
  id,
  title,
  targetId = "",
  targetPosition = 0,
  isSticky = false,
  stickyTop = 164,
  showAtScrollPosition = false,
}) {
  const titleMarkup = <h3 className="no-margin">{title}</h3>;
  const renderSubMenu = (sticky = false) => (
    <SubMenu
      id={`${id ? id : "it-connection-submenu"}${sticky ? "-sticky" : ""}`}
      isSticky={isSticky}
      className="w3-right"
      linkClassName="w3-large w3-block pad-lr"
      activeClassName="active w3-light-grey"
      initialActiveId={"vodafone-account"}
      items={[
        {
          name: "Vodafone Account",
          href: "vodafone-account",
          resource: Resources.Organisation.WorkerWizard.ADConnections,
        },
        {
          name: "AN-Portal Zugang",
          href: "anp-zugang",
          resource: Resources.Organisation.WorkerWizard.ANPAccount,
        },
        {
          name: "iProv Zugang",
          href: "iprov-zugang",
          resource: Resources.Organisation.WorkerWizard.PTConnections,
        },
        {
          name: "Delphi Zugänge",
          href: "delphi-zugaenge",
          resource: Resources.Organisation.WorkerWizard.DelphiConnections,
        },
        {
          name: "Jarvis Zugang",
          href: "jarvis-zugang",
          resource: Resources.Organisation.WorkerWizard.JarvisConnection,
        },
      ]}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      bottomId={targetId}
    />
  );

  let markup = (
    <div
      id="it-connection-header"
      className="pad-big-lr pad-big-tb w3-white w3-border-bottom"
    >
      {renderSubMenu()}
      {titleMarkup}
    </div>
  );

  if (isSticky) {
    const stickyHeaderMarkup = (
      <ScrollPositionedHeader
        targetId={"it-connection-header"}
        top={stickyTop}
        stopTargetId={"jarvis-zugang"}
        stopTop={-60}
        width="100%"
      >
        <div
          className="pad-big-lr pad-big-tb w3-border w3-white"
          style={{
            position: "relative",
            left: "-1px",
            top: "-1px",
            width: "1160px",
          }}
        >
          <div
            className="w3-white"
            style={{
              position: "absolute",
              top: "-19px",
              left: "-1px",
              height: "18px",
              width: "calc(100% + 2px)",
            }}
          />
          {renderSubMenu(true)}
          {titleMarkup}
        </div>
      </ScrollPositionedHeader>
    );
    markup = (
      <>
        {markup}
        {stickyHeaderMarkup}
      </>
    );
  } else if (showAtScrollPosition) {
    markup = (
      <AppearOnScrollPosition targetId={targetId} top={targetPosition}>
        <div
          className="w3-white"
          style={{
            margin: "0 -1px",
            height: "18px",
            width: "calc(100% + 2px)",
          }}
        />
        <div className="w3-border-top">{markup}</div>
      </AppearOnScrollPosition>
    );
  }

  return markup;
}

ITConnectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  targetId: PropTypes.string,
  targetPosition: PropTypes.number,
  isSticky: PropTypes.bool,
  showAtScrollPosition: PropTypes.bool,
};

function SubMenu({
  id,
  isSticky = false,
  className = "",
  linkClassName = "sub-menu-item",
  activeClassName = "active",
  initialActiveId,
  items = [],
  bottomId = "",
}) {
  const [activeId, setActiveId] = React.useState(initialActiveId);
  const [refPosition, setRefPosition] = React.useState(null);
  const [clickData, setClickData] = React.useState({
    id: null,
    timestamp: 0,
  });

  React.useEffect(() => {
    if (!isSticky) {
      return;
    }
    const handleScroll = () => {
      const tolerance = 50;
      const menuTargets = [];
      items.forEach((item, index) => {
        const target = document.getElementById(`${item.href}`);
        if (target) {
          const top = target.getBoundingClientRect().y;
          menuTargets.push({
            target,
            id: item.href,
            top,
          });
          if (index > 0 && menuTargets.length > 1) {
            menuTargets[index - 1].btm = top;
          }
          // if (index === menuTargets.length - 1) {
          //   const btmTarget = document.getElementById(bottomId);
          //   if (btmTarget) {
          //     menuTargets[index].btm = btmTarget.getBoundingClientRect().y;
          //   }
          // }
        }
      });
      let scrollingDown = true;
      let update = false;
      for (let index = 0; index < menuTargets.length; index++) {
        const { id, top, btm } = menuTargets[index];
        if (index === 0) {
          if (refPosition === null) {
            setRefPosition(top);
          } else {
            scrollingDown = top < refPosition;
            setRefPosition(top);
          }
        }
        // (id === "anp-zugang") && console.log(`SubMenu - checking ${id}: top=${top} btm=${btm}`,
        //   "activeId:", activeId, "scrollingDown:", scrollingDown);

        if (scrollingDown) {
          if (top <= 0 && top >= -tolerance && activeId !== id) {
            update = true;
          }
        } else if (btm) {
          if (btm >= 0 && btm <= tolerance && activeId !== id) {
            update = true;
          }
        }
        if (update) {
          // determine a menu item was clicked to start scroll
          let justClicked = false;
          if (clickData.timestamp > 0) {
            // calculate time ellapsed since last click
            const now = Date.now();
            const interval = now - clickData.timestamp;
            justClicked = interval < 500; // time is in ms
            // console.log("Time since last click:", interval, now, clickData.timestamp);
          }
          if (!justClicked) {
            // console.log("SubMenu: setting activeId to", id);
            setActiveId(id);
          }
          break;
        }
      }

      if (!scrollingDown && !update && activeId === "") {
        setActiveId(menuTargets[menuTargets.length - 1].id);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isSticky, items, activeId, bottomId, refPosition, clickData]);

  const onClick = React.useCallback(
    (currentId) => {
      setClickData({
        id: currentId,
        timestamp: Date.now(),
      });
      setActiveId(currentId);
    },
    [clickData],
  );

  return (
    <div
      id={id}
      className={`sub-menu ${className} w3-border`}
      style={{ display: "flex", height: "36px", alignItems: "stretch" }}
    >
      {items.map((item, index) => (
        <PermissionResource
          key={`sub-menu-item-${index}`}
          resource={item.resource}
        >
          <InpageNavLink
            data-test-id={`tablink-${item.href}`}
            className={classnames(linkClassName, {
              "w3-border-left": index > 0,
            })}
            to={item.href}
            activeClassName={classnames(linkClassName, activeClassName, {
              "w3-border-left": index > 0,
            })}
            currentTo={activeId}
            setCurrentTo={onClick}
            style={{
              flexShrink: 0,
              flexGrow: 0,
              width: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item.name}
          </InpageNavLink>
        </PermissionResource>
      ))}
    </div>
  );
}

SubMenu.propTypes = {
  id: PropTypes.string,
  isSticky: PropTypes.bool,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
  activeClassName: PropTypes.string,
  initialActiveId: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      href: PropTypes.string,
    }),
  ).isRequired,
};
